import { useEffect, useState, useCallback } from "react";

// LocalStorage key — per-user offset (in milliseconds) added to Date.now()
// to get the user's "real" current time. 0 = use system clock.
const STORAGE_KEY = "crm_time_offset_ms";

function readOffset() {
    try {
        const v = window.localStorage.getItem(STORAGE_KEY);
        const n = v ? parseInt(v, 10) : 0;
        return Number.isFinite(n) ? n : 0;
    } catch {
        return 0;
    }
}

function writeOffset(ms) {
    try {
        window.localStorage.setItem(STORAGE_KEY, String(ms || 0));
        // Broadcast within the tab (storage event only fires across tabs)
        window.dispatchEvent(new CustomEvent("crm-time-offset-changed", { detail: ms }));
    } catch {
        // ignore
    }
}

/**
 * Hook for reading + setting a user-defined offset from system time.
 * Returns { offsetMs, now, setOffsetMs, setCurrentTime, reset }.
 */
export function useTimeOffset() {
    const [offsetMs, setOffsetMsState] = useState(readOffset());

    // Keep state in sync if changed elsewhere in the app
    useEffect(() => {
        const onChange = (e) => {
            const next = typeof e?.detail === "number" ? e.detail : readOffset();
            setOffsetMsState(next);
        };
        const onStorage = (e) => {
            if (e.key === STORAGE_KEY) setOffsetMsState(readOffset());
        };
        window.addEventListener("crm-time-offset-changed", onChange);
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener("crm-time-offset-changed", onChange);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    const now = useCallback(() => new Date(Date.now() + offsetMs), [offsetMs]);

    const setOffsetMs = useCallback((ms) => {
        const n = Math.round(Number(ms) || 0);
        writeOffset(n);
        setOffsetMsState(n);
    }, []);

    // Given an HH:MM string + a date (default: today's local date),
    // compute the offset needed so "now" equals that wall-clock time.
    const setCurrentTime = useCallback((hhmmString, dateBase = new Date()) => {
        if (!hhmmString || !/^\d{2}:\d{2}$/.test(hhmmString)) return;
        const [h, m] = hhmmString.split(":").map(Number);
        const target = new Date(dateBase);
        target.setHours(h, m, 0, 0);
        setOffsetMs(target.getTime() - Date.now());
    }, [setOffsetMs]);

    const reset = useCallback(() => setOffsetMs(0), [setOffsetMs]);

    return { offsetMs, now, setOffsetMs, setCurrentTime, reset };
}

// Read-only helper for non-hook callers
export function getCurrentTime() {
    return new Date(Date.now() + readOffset());
}
